import './ServiceRequestsMyActivity.scss';

import React, {
  useMemo,
  useRef,
  useState,
} from 'react';

import { Segmented } from 'antd';
import { PiBell } from 'react-icons/pi';

import HeaderIcon from '../../../../components/Header/actions/HeaderIcon';
import Header from '../../../../components/Header/Header';
import {
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { checkTQConnectAccess } from '../../../../shared/tqConnectUtils';
import {
  ROUTES,
  TQConnectServiceRequestsMyActivityTabs,
} from '../../../../types/routes';
import {
  TQServiceRequestTypeValue,
} from '../../../../types/tqConnectTypes/service-requests';
import TQConnectInfoCard
  from '../../components/TQConnectInfoCard/TQConnectInfoCard';
import TQCreateNewRequestButton
  from '../../components/TQCreateNewRequestButton';
import TQServiceRequestList
  from '../../components/TQServiceRequestList/TQServiceRequestList';

const TAB_TITLES: Record<TQConnectServiceRequestsMyActivityTabs, string> = {
  [TQConnectServiceRequestsMyActivityTabs.REQUEST_RAISED]: 'Requests Raised',
  [TQConnectServiceRequestsMyActivityTabs.REQUEST_RECEIVED]:
    'Requests Received',
  [TQConnectServiceRequestsMyActivityTabs.RESPONDED]: 'Help Offered',
};

const ServiceRequestsMyActivity: React.FC = () => {
  const navigate = useAppNavigate();
  const handleBack = () => {
    navigate.goBack();
  };

  const searchParams = new URLSearchParams(window.location.search);
  const initialTab = searchParams.get(
    'tab',
  ) as TQConnectServiceRequestsMyActivityTabs;

  const { tqConnectProfileCompletionResponses } = useAppSelector(
    (state) => state.user,
  );

  const { userAccessType } = checkTQConnectAccess();

  const isProfileNotCompleted = useMemo(() => {
    return (
      userAccessType === 'customer' &&
      (!tqConnectProfileCompletionResponses ||
        tqConnectProfileCompletionResponses.length === 0)
    );
  }, [tqConnectProfileCompletionResponses, userAccessType]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] =
    useState<TQConnectServiceRequestsMyActivityTabs>(
      initialTab &&
        Object.values(TQConnectServiceRequestsMyActivityTabs).includes(
          initialTab,
        )
        ? initialTab
        : TQConnectServiceRequestsMyActivityTabs.REQUEST_RAISED,
    );

  const onSelectTab = (tab: TQConnectServiceRequestsMyActivityTabs) => {
    setActiveTab(tab);
  };

  const segmentedOptions = Object.values(
    TQConnectServiceRequestsMyActivityTabs,
  ).map((tabKey) => ({
    label: TAB_TITLES[tabKey],
    value: tabKey,
  }));

  const handleNotificationClick = () => {
    navigate(ROUTES.TQ_CONNECT_RECENT_ACTIVITY, {
      requestType: activeTab as TQServiceRequestTypeValue,
    });
  };

  return (
    <div className="service-requests-my-activity__page page__container">
      <div className="page__content">
        <Header
          title=""
          handleBack={handleBack}
          actionItems={[
            <HeaderIcon
              key="notification"
              icon={<PiBell size={22} aria-hidden />}
              onClick={handleNotificationClick}
            />,
          ]}
        />
        {isProfileNotCompleted ? (
          <TQConnectInfoCard />
        ) : (
          <>
            <h2 className="service-requests-my-activity__page-title">
              My Activity
            </h2>

            <div className="service-requests-my-activity__page__create-new-request-button">
              <TQCreateNewRequestButton />
            </div>
            <div className="service-requests-my-activity__page__segmented-wrapper">
              <Segmented
                block
                options={segmentedOptions}
                value={activeTab}
                onChange={(key: string | number) =>
                  onSelectTab(key as TQConnectServiceRequestsMyActivityTabs)
                }
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>

            <div className="page__content__main">
              <div
                ref={scrollContainerRef}
                className="page__content__scroll"
                id="service-requests-discover-scroll-container">
                <TQServiceRequestList
                  requestType={activeTab}
                  key={`service-requests-my-activity-tab-${activeTab}`}
                  scrollContainerRef={scrollContainerRef}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceRequestsMyActivity;
